import { useCallback, useEffect, useRef, type Dispatch } from 'react';
import { useRecoilValue } from 'recoil';
import prop from 'ramda/es/prop';
import uniqBy from 'ramda/es/uniqBy';
import { logger } from 'modules/logger';
import type { LoadedReportWorklistItem, ReportTemplate } from 'hooks/useCurrentCaseReport';
import type { AutoloadTemplateState } from 'hooks/useAutoloadTemplate';
import { useTemplateInsertionState } from 'hooks/useTemplateInsertionState';
import { InsertedTemplateDataSource, templateInsertRequestState } from '../state';
import type { ReportStatuses } from '../state';
import { getAllPicklistsForTemplate, normalizeSlateContent, convertFieldsToUnified } from './utils';
import { isTemplateInsertionForbidden } from './reportStatusGuards';
import type { ActionType } from './fieldsReducer';

/**
 * Owns template insertion: the imperative `handleInsertTemplate` (writes the template's macros +
 * activateTemplateID into reducer state, seeds picklists, and hands normalized content to the editor)
 * and the effect that drives it off the recoil `templateInsertRequest`, applying the worklist-item,
 * disabled, status, and autoload guards and de-duping repeated requests.
 *
 * Note: syncing inserted macros into the picklist store stays in Fields.Context (a separate effect on
 * reducer `macros`); this hook only writes those macros into reducer state.
 */
export interface UseTemplateWorkflowParams {
  template: ReportTemplate | undefined;
  worklistItem: LoadedReportWorklistItem;
  status: ReportStatuses;
  autoloadTemplateState: AutoloadTemplateState | undefined;
  isDisabled: boolean;
  dispatch: Dispatch<ActionType>;
  insertTemplate: (template: ReportTemplate) => boolean;
  markNextReportChangeAsTemplateApply: () => void;
  clearPendingReportChangeType: () => void;
  setPicklists: (picklists: ReturnType<typeof getAllPicklistsForTemplate>) => void;
  setHasReportContentChanged: (value: boolean) => void;
}

export const useTemplateWorkflow = ({
  template,
  worklistItem,
  status,
  autoloadTemplateState,
  isDisabled,
  dispatch,
  insertTemplate,
  markNextReportChangeAsTemplateApply,
  clearPendingReportChangeType,
  setPicklists,
  setHasReportContentChanged,
}: UseTemplateWorkflowParams) => {
  const templateInsertRequest = useRecoilValue(templateInsertRequestState);
  const { clearTemplateInsertRequestIfMatches } = useTemplateInsertionState();
  // Guards against re-processing the same insert request
  const handledTemplateInsertRequestIdRef = useRef<string | null>(null);

  const handleInsertTemplate = useCallback(() => {
    if (template == null) return;

    logger.info('[useTemplateWorkflow] handleInsertTemplate triggered with template: ', {
      templateId: template.id,
      templateName: template.name,
      worklistItemTemplateId: worklistItem?.report?.template?.id,
      worklistItemTemplateName: worklistItem?.report?.template?.name,
    });

    const templateMacros = template.macros ?? [];
    const worklistMacros = worklistItem.report?.macros ?? [];

    dispatch({
      type: 'insertTemplate',
      payload: {
        macros: uniqBy(prop('smid'), [...templateMacros, ...worklistMacros]),
        activateTemplateID: template.id,
      },
    });

    setPicklists(getAllPicklistsForTemplate(template));
    const headingContent = normalizeSlateContent(
      template.content ?? convertFieldsToUnified(template.sections)
    );
    const templateToInsert = {
      ...template,
      content: headingContent,
    } as const;

    markNextReportChangeAsTemplateApply();
    let didInsertTemplate = false;
    try {
      didInsertTemplate = insertTemplate(templateToInsert);
    } catch (error) {
      clearPendingReportChangeType();
      throw error;
    }
    if (!didInsertTemplate) {
      clearPendingReportChangeType();
      return;
    }
    setHasReportContentChanged(false);
  }, [
    template,
    worklistItem?.report?.template?.id,
    worklistItem?.report?.template?.name,
    worklistItem.report?.macros,
    dispatch,
    setPicklists,
    insertTemplate,
    markNextReportChangeAsTemplateApply,
    clearPendingReportChangeType,
    setHasReportContentChanged,
  ]);

  useEffect(() => {
    if (templateInsertRequest == null) return;
    const { requestId } = templateInsertRequest;

    if (handledTemplateInsertRequestIdRef.current === requestId) {
      clearTemplateInsertRequestIfMatches(requestId);
      return;
    }

    logger.info('[useTemplateWorkflow] Processing template insertion request: ', {
      requestId: templateInsertRequest.requestId,
      requestedTemplateId: templateInsertRequest.templateId,
      requestSource: templateInsertRequest.source,
      requestedWorklistItemSmid: templateInsertRequest.worklistItemSmid,
      currentWorklistItemSmid: worklistItem.smid,
      autoloadTemplateState,
      status,
      isDisabled,
    });

    if (templateInsertRequest.worklistItemSmid !== worklistItem.smid) {
      handledTemplateInsertRequestIdRef.current = requestId;
      clearTemplateInsertRequestIfMatches(requestId);
      return;
    }
    if (isDisabled) return;
    if (template == null) return;
    if (template.id !== templateInsertRequest.templateId) return;

    if (isTemplateInsertionForbidden(status)) return;

    // Wait for autoload decision before applying any template.
    // This prevents stale template state (e.g. from a previous case) from being inserted
    // while autoload resolution is still in progress for the current case.
    if (autoloadTemplateState === 'pending') return;

    // If autoloading was skipped (e.g. because the report already has saved content),
    // don't let the template overwrite the existing report.
    if (
      autoloadTemplateState === 'skipped' &&
      templateInsertRequest.source === InsertedTemplateDataSource.Autoload
    ) {
      handledTemplateInsertRequestIdRef.current = requestId;
      clearTemplateInsertRequestIfMatches(requestId);
      return;
    }

    try {
      handleInsertTemplate();
    } catch (error) {
      logger.error(
        '[useTemplateWorkflow] Error occurred while inserting template from request',
        {
          requestId,
          templateId: templateInsertRequest.templateId,
          source: templateInsertRequest.source,
          worklistItemSmid: templateInsertRequest.worklistItemSmid,
        },
        error
      );
    } finally {
      handledTemplateInsertRequestIdRef.current = requestId;
      clearTemplateInsertRequestIfMatches(requestId);
    }
  }, [
    templateInsertRequest,
    worklistItem.smid,
    template,
    autoloadTemplateState,
    isDisabled,
    status,
    handleInsertTemplate,
    clearTemplateInsertRequestIfMatches,
  ]);

  return { handleInsertTemplate };
};
